/**
 * Service-request display constants.
 *
 * Single source of truth for:
 *   - Document type labels and upload rules
 *   - Workflow stepper steps and state-to-step mapping
 *   - Per-service-type step definitions (container/presentation pattern)
 *   - UI phase helpers (which screen to show per workflow state)
 */
import type { CustomerServiceRequestWorkflowState } from '@/api/service-requests';

// ── Document types ────────────────────────────────────────────────────────────

export const DOC_TYPE_LABELS: Record<string, string> = {
  form_16:             'Form 16',
  bank_statement:      'Bank Statement',
  aadhaar:             'Aadhaar',
  pan:                 'PAN Card',
  passport:            'Passport',
  salary_certificate:  'Salary Certificate',
  investment_proof:    'Investment Proof',
  rent_receipt:        'Rent Receipt',
  house_property_docs: 'House Property Docs',
  capital_gains_docs:  'Capital Gains Docs',
  ais:                 'Annual Information Statement (AIS)',
  tis:                 'Taxpayer Information Summary (TIS)',
  form_26as:           'Form 26AS',
  prior_itr:           'Prior Year ITR',
  other:               'Other Document',
};

/**
 * Values the backend `DocumentType` enum accepts on upload (app/models/enums.py).
 * Anything else (admin-typed slots like "Msme") is rejected with 422, so it is
 * sent as `other` + a label instead. Keep in sync with the backend enum.
 */
export const BACKEND_DOC_TYPES = new Set([
  'draft_computation', 'acknowledgement', 'form_16', 'bank_statement', 'aadhaar', 'pan',
  'passport', 'salary_certificate', 'investment_proof', 'rsu_related', 'esop_related',
  'insurance', 'housing_loan', 'education_loan', 'nps', 'rent_receipt',
  'house_property_docs', 'capital_gains_docs', 'ais', 'tis', 'form_26as', 'ecas',
  'term_insurance', 'health_insurance', 'other',
]);

/** Documents sourced from ERI portal — customers don't upload these. */
export const INTERNAL_DOC_TYPES = new Set(['ais', 'tis', 'form_26as', 'form26as']);

export const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'doc', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'] as const;
export const MAX_FILE_BYTES      = 20 * 1024 * 1024; // 20 MB

/** `accept` attr derived from ALLOWED_EXTENSIONS — keeps the file picker in sync with validation. */
export const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',');

// ── Top-level workflow stepper (7 stages, matches QuestionnaireStepper) ───────

export interface StepperStep {
  /** Full label shown on desktop. */
  label: string;
  /** Abbreviated label shown on mobile (≤ sm breakpoint). */
  shortLabel: string;
}

/**
 * 7-stage progress indicator that mirrors the complete FinminOS service journey.
 * Matches the step names and count from the original QuestionnaireStepper.
 */
export const STEPPER_STEPS: StepperStep[] = [
  { label: 'Information Retrieval', shortLabel: 'Info'        },
  { label: 'Document Upload',       shortLabel: 'Docs'        },
  { label: 'Provider Assignment',   shortLabel: 'Assignment'  },
  { label: 'Platform Clarification',shortLabel: 'Clarify'     },
  { label: 'Draft Evaluation',      shortLabel: 'Evaluation'  },
  { label: 'Payment Completion',    shortLabel: 'Payment'     },
  { label: 'Service Completion',    shortLabel: 'Done'        },
];

/**
 * Maps a backend workflow state to the 1-based index of the currently active
 * stepper step. Aligns with the original `deriveStepperStep` logic.
 */
export function deriveStepperIndex(
  state: CustomerServiceRequestWorkflowState | string | null | undefined,
): number {
  switch (state) {
    case 'service_selected':
      return 1; // Information Retrieval — AI analysing

    case 'docs_start':
      return 2; // Document Upload

    case 'provider_assignment_start':
      return 3; // Provider Assignment — admin searching

    case 'provider_assignment_complete':
    case 'platform_clarification':
      return 4; // Platform Clarification — chat with expert

    case 'draft_evaluation':
      return 5; // Draft Evaluation

    case 'acknowledgement_submission':
    case 'acknowledgement_submitted':
      return 6; // Payment Completion

    case 'service_request_complete':
    case 'service_delivered':
      return 7; // Service Completion
    default:
      return 1;
  }
}

// ── Service-step types (used by ServiceStepsContainer + ServiceStepsView) ----
//
// Step definitions are fetched live from GET /api/v1/service-steps.
// ServiceStepsContainer maps backend steps to ResolvedServiceStep[] and
// passes the list to ServiceStepsView for rendering.

export type StepStatus = 'completed' | 'current' | 'upcoming';

export interface ResolvedServiceStep {
  title: string;
  desc: string;
  status: StepStatus;
}

// ── UI phase helpers ----------------------------------------------------------

/** States that render the AI-processing / waiting screen. */
export const PROCESSING_STATES = new Set<string>(['service_selected']);

/** States that render the document-upload screen. */
export const UPLOAD_STATES = new Set<string>(['docs_start']);

/** States that render the full provider workspace. */
export const WORKSPACE_STATES = new Set<string>([
  'provider_assignment_start',
  'provider_assignment_complete',
  'platform_clarification',
  'draft_evaluation',
  'acknowledgement_submission',
  'acknowledgement_submitted',
  'service_request_complete',
  'service_delivered',
]);
